import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Input, RadioGroup, RadioGroupItem } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { FieldGrid } from '../../section-shell';
import type { ContractFormValues } from '../../contract-form';

/** Owner type options matching legacy `domain_extra_fields.for`. */
const FOR_OPTIONS = [
  { value: 'canhan', label: 'Cá Nhân' },
  { value: 'doanhnghiep', label: 'Doanh Nghiệp' },
] as const;

interface VnnicCustomerFieldsProps {
  /** Absolute path prefix: `products.{i}.service_domain_metadata`. */
  metaPath: string;
}

/**
 * Step 1 of the VNNIC registrar form: owner (customer) information.
 * Writes into `service_domain_metadata.domain_extra_fields.owner*` fields.
 * Mirrors legacy CustomerInformation component fields (name, ID/tax, email x2,
 * phone, fax). Address (province/district/ward/country) omitted — requires a
 * PermanentAddress lookup component not yet available in this workspace.
 */
export function VnnicCustomerFields({ metaPath }: VnnicCustomerFieldsProps) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const extraPath = `${metaPath}.domain_extra_fields`;

  const forRegister = (useWatch({ control, name: `${extraPath}.for` as never }) as string) ?? 'canhan';

  const field = (key: string) => `${extraPath}.${key}` as never;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {/* Owner type: individual vs business */}
      <Field label="Đối tượng đăng ký">
        {() => (
          <RadioGroup
            value={forRegister}
            onValueChange={(v) => setValue(field('for'), v as never, { shouldDirty: true })}
            style={{ display: 'flex', gap: 'var(--vn-space-3)' }}
          >
            {FOR_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}
              >
                <RadioGroupItem value={opt.value} />
                {opt.label}
              </label>
            ))}
          </RadioGroup>
        )}
      </Field>

      {/* Full name */}
      <Controller
        control={control}
        name={field('ownerName')}
        render={({ field: f }) => (
          <Field label="Họ và tên chủ thể">
            {(p) => (
              <Input {...p} type="text" placeholder="Họ và tên"
                value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
            )}
          </Field>
        )}
      />

      {/* ID number (individual) or tax code (business) */}
      {forRegister === 'canhan' ? (
        <Controller
          control={control}
          name={field('ownerID_Number')}
          render={({ field: f }) => (
            <Field label="Số CMND / CCCD">
              {(p) => (
                <Input {...p} type="text" placeholder="Số CMND / CCCD"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
      ) : (
        <Controller
          control={control}
          name={field('ownerTaxCode')}
          render={({ field: f }) => (
            <Field label="Mã số thuế">
              {(p) => (
                <Input {...p} type="text" placeholder="Mã số thuế"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
      )}

      {/* Province — plain text fallback (no PermanentAddress lookup widget here) */}
      <Controller
        control={control}
        name={field('ownerProvince')}
        render={({ field: f }) => (
          <Field label="Tỉnh / Thành phố">
            {(p) => (
              <Input {...p} type="text" placeholder="Tỉnh / Thành phố"
                value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
            )}
          </Field>
        )}
      />

      {/* Street address */}
      <Controller
        control={control}
        name={field('ownerAddress')}
        render={({ field: f }) => (
          <Field label="Địa chỉ">
            {(p) => (
              <Input {...p} type="text" placeholder="Số nhà, đường, phường/xã"
                value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
            )}
          </Field>
        )}
      />

      <FieldGrid min={200}>
        <Controller
          control={control}
          name={field('ownerEmail1')}
          render={({ field: f }) => (
            <Field label="Email chính">
              {(p) => (
                <Input {...p} type="email" placeholder="email@example.com"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('ownerEmail2')}
          render={({ field: f }) => (
            <Field label="Email phụ">
              {(p) => (
                <Input {...p} type="email" placeholder="email2@example.com"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('ownerPhone')}
          render={({ field: f }) => (
            <Field label="Điện thoại">
              {(p) => (
                <Input {...p} type="text" placeholder="0901234567"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
        <Controller
          control={control}
          name={field('ownerFax')}
          render={({ field: f }) => (
            <Field label="Fax">
              {(p) => (
                <Input {...p} type="text" placeholder="Số fax"
                  value={(f.value as string) ?? ''} onChange={f.onChange} onBlur={f.onBlur} name={f.name} ref={f.ref} />
              )}
            </Field>
          )}
        />
      </FieldGrid>
    </div>
  );
}
